import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface SEOTextBlockProps {
  title: string;
  content: string;
}

export function SEOTextBlock({ title, content }: SEOTextBlockProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <section 
      className="bg-[hsl(213_50%_15%)] text-primary-foreground/30 border-t border-primary-foreground/5"
      aria-label="Additional information"
    >
      <div className="container py-4">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 text-xs text-primary-foreground/60 hover:text-primary-foreground/80 transition-colors w-full justify-between"
          aria-expanded={isExpanded}
        >
          <span className="font-medium">{title}</span>
          <ChevronDown 
            className={cn(
              "w-4 h-4 transition-transform duration-300",
              isExpanded && "rotate-180"
            )} 
          />
        </button>
        
        {/* SEO content - always in DOM for search engines, visually collapsed */}
        <div 
          className={cn(
            "overflow-hidden transition-all duration-300 ease-in-out",
            isExpanded ? "max-h-[500px] opacity-100 mt-3" : "max-h-0 opacity-0"
          )}
          // Using max-height animation instead of display:none to keep content crawlable
        >
          <div className="text-[10px] leading-relaxed text-primary-foreground/25 space-y-2">
            {content.split('\n\n').map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        </div>
        
        {/* Hidden content for SEO - visible to crawlers but not users */}
        <div 
          className="sr-only"
          aria-hidden="false"
        >
          {content}
        </div>
      </div>
    </section>
  );
}
